import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { hotjar } from 'react-hotjar';

import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import { trakrefFontFamily, validateEmail } from '../../utils';

import SSOForm from './SSOForm';
import LoginForm from './LoginForm';
import ForgotPasswordModal from './ForgetPasswordModal';

import "./Login.css";
import { White } from '../../color_utils';
import { Button } from '@mui/material';
import Enums from "../../enums";

const Login = ({
    onChange,
    setCurrentLanguage
}) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [EmailForgotField, setEmailForgotField] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isForgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [SSOLogin, setSSOLogin] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [forgotButtonModalOpen, setforgotButtonModalOpen] = useState(false);

    useEffect(() => {
        const storedUsername = localStorage.getItem('remember-me-username');
        if (storedUsername) {
            setUsername(storedUsername);
            setRememberMe(true);
        }
        hotjar.stateChange(document.URL + '#Login');
    }, [])

    const login = async (e) => {
        setLoading(true);
        e.preventDefault();
        // Attempt to authenticate user
        const body = {
            Username: username,
            Password: password
        };

        const response = await fetch(Routes.Account.Login(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
        });

        if (response.status === 429) {
            const accountLockedMessage = localStorage.getItem('account-locked-message');
            if (accountLockedMessage) {
                setError(accountLockedMessage);
                setLoading(false);
                return;
            }

            const retryAfterValue = response.headers.get('retry-after');
            if (retryAfterValue) {
                const waitSeconds = parseInt(retryAfterValue, 10);
                const waitMinutes = waitSeconds / 60;
                const roundedMinutes = Math.ceil(waitMinutes);
                setError(`Please try again in about ${roundedMinutes} minute(s).`);
            }
            setLoading(false);
            return
        }

        const data = await response.json();
        localStorage.removeItem('account-locked-message');
        setLoading(false);

        if (data.error) {
            if (data.error.toLowerCase().includes("account locked")) {
                localStorage.setItem('account-locked-message', data.error);
            }
            setError(data.error);
        } else {
            // console.log(data)
            // If successful, set properties and pass back to main page
            const isProduction = (localStorage.getItem("isprod") === 'true');
            const instance = data.instances.find(x => x.id === data.instanceId);
            const account_type = Object.keys(Enums.AccountTypes)
                .find(key => Enums.AccountTypes[key] === instance.accountTypeId);

            UserInfo.setUserName(data.userName);
            UserInfo.setUserId(data.id);
            UserInfo.setFirstName(data.firstName);
            UserInfo.setLastName(data.lastName);
            UserInfo.setInstanceId(data.instanceId);
            UserInfo.setInstanceName(instance.name);
            UserInfo.setNativeInstanceId(data.nativeInstanceId);
            UserInfo.setInstances(data.instances);
            UserInfo.setLocations(data.locations);
            UserInfo.setGroupId(data.groupId);
            UserInfo.setState(data.state);
            UserInfo.setPreferedLeakDetection(data.preferredLeakDetectionMethodID);
            UserInfo.setFullPayload(data);
            UserInfo.setWeightUnits(data.weightUnits);
            UserInfo.setIsOptedIn(data.isOptedIn);
            UserInfo.setLbsOnlyDecimal(data.onlyWeightDecimal);
            UserInfo.setUserCulture(data.culture);
            UserInfo.setIsProduction(isProduction);
            UserInfo.setRoles(data.roles);
            UserInfo.setAccountType(data.accountTypeId);
            UserInfo.setNativeAccountTypeId(data.nativeAccountTypeId);
            UserInfo.setIsWriteIntech(data.isWriteInTech);
            UserInfo.setRoles(data.roles);
            UserInfo.setAllowAnalytics(data.allowAnalytics);
            UserInfo.setIsTempPassword(data.isTempPassword);
            UserInfo.setPasswordResetCode(data.passwordResetCode);
            UserInfo.setIdleTimeout(data.idleTimeout);
            UserInfo.setLoginTypeId(data.loginTypeId);

            // Identify Refiner user upon successful login
            _refiner('identifyUser', {
                id: data.id,
                email: data.email,
                name: data.userName,
                account_type,
                user_role: data.group,
                host_url: window.location.href,
                country: instance.country || '',
                environment: window.location.host,
            });

            setCurrentLanguage(data.culture);
            onChange();

            hotjar.stateChange(document.URL);
            
            if (rememberMe) {
                localStorage.setItem('remember-me-username', username);
            }
            else {
                localStorage.removeItem('remember-me-username');
            }
        }
    };

    const forgotPassword = async () => {
        const emailIsValid = EmailForgotField.length > 1 && validateEmail(EmailForgotField);
        if (!emailIsValid) {
            return toast.error('Please use valid email format', { duration: 5000 });
        }

        setForgotPasswordLoading(true);

        let Environment = "";
        if (document.URL.includes("v4betatrakref.azurewebsites.net"))
            Environment = "BETA"
        else if (document.URL.includes("v4qa.trakref.com"))
            Environment = "QA"
        else if (document.URL.includes("productiontrakref-staging.azurewebsites.net"))
            Environment = "STAGING"
        else if (document.URL.includes("v4.trakref.com"))
            Environment = "PROD"
        else
            Environment = "LOCALHOST"

        const Body = {
            Email: EmailForgotField,
            URL_Type: Environment
        };

        const response = await fetch(Routes.Account.ForgotPassword(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(Body)
        });

        if (response.status === 200) {
            setforgotButtonModalOpen(false);
            toast.success('Reset Email has been sent to your inbox.', { duration: 5000 });
        } else {
            toast.error('Email Reset Failed. Please try again later.', { duration: 5000 });
        }
        setForgotPasswordLoading(false);
    };

    const handleKeyDown = e => e.keyCode === 13 ? forgotPassword() : null;

    return (
        <>
            <div className="main flex f-center f-row contain">
                <div className="imagePlaceholder flex f-col">
                    <img
                        className="bottomMargin"
                        style={{
                            height: "20px"
                        }}
                        src={'/images/whitelogo_2025.svg'}
                        alt="logo"
                    />
                    <div className="carousel-body flex f-col">
                        <p className="carousel-title bottomMargin">Helping you better manage HVACR & refrigerant</p>
                        <p className="carousel-text bottomMargin">
                            As refrigerant geeks with more than 25+ years experience in HVACR, Trakref provides industry-leading software solutions and compliance updates.
                        </p>
                        <Button
                            variant={"outlined"}
                            onClick={() => window.location.href = `https://trakref.com/`}
                            sx={{
                                '&.MuiButton-outlined': {
                                    color: White
                                },
                                '&.MuiButton-root': {
                                    fontFamily: trakrefFontFamily,
                                    letterSpacing: '1.2px',
                                    fontSize: '15px',
                                    opacity: '100%',
                                    border: `1px solid ${White}`
                                },
                                width: "188px",
                                height: "52px"
                            }}
                        >
                            LEARN MORE
                        </Button>
                    </div>
                </div>
                <div className="Login flex f-center">
                    <LoginForm
                        login={login}
                        username={username}
                        setUsername={setUsername}
                        error={error}
                        setError={setError}
                        password={password}
                        setPassword={setPassword}
                        loading={loading}
                        setRememberMe={setRememberMe}
                        rememberMe={rememberMe}
                        setforgotButtonModalOpen={setforgotButtonModalOpen}
                        forgotButtonModalOpen={forgotButtonModalOpen}
                    />
                </div>
            </div>

            <ForgotPasswordModal
                forgotButtonModalOpen={forgotButtonModalOpen}
                setforgotButtonModalOpen={setforgotButtonModalOpen}
                EmailForgotField={EmailForgotField}
                setEmailForgotField={setEmailForgotField}
                handleKeyDown={handleKeyDown}
                error={error}
                forgotPassword={forgotPassword}
                isLoading={isForgotPasswordLoading}
            />
        </>
    );
};

export default Login;
